import { useGetIndustryList } from "@/api/services/global/industry";
import { InnerBox } from "@/components/elements/InnerBox";
import { Switch } from "@/components/elements/Switch";
import { CompanyFormTitle } from "@/features/company/components/company-info/CompanyFormTitle";
import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { text } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { Stack, Typography } from "@mui/material";
import { FC, useCallback } from "react";
import { useController, useFormContext } from "react-hook-form";

/**
 * 取り扱う分野 — company_user toggles which 分野 (industry) the company manages/views.
 * Stored as `hiddenIndustryIds` (ids turned OFF); empty means every industry is enabled.
 */
export const CompanyHandledIndustryForm: FC = () => {
  const { control } = useFormContext<TCompanyForm>();
  const { field } = useController({ control, name: "hiddenIndustryIds", defaultValue: [] });
  const hiddenIndustryIds = field.value ?? [];

  const { data: publishedIndustries } = useGetIndustryList();

  const handleToggle = useCallback(
    (industryId: number, checked: boolean) => {
      const current = field.value ?? [];
      if (checked) {
        field.onChange(current.filter((id) => Number(id) !== Number(industryId)));
      } else {
        field.onChange([...current, Number(industryId)]);
      }
    },
    [field],
  );

  return (
    <InnerBox title={<CompanyFormTitle text="取り扱う分野" />} showDivider={false}>
      <Stack gap={3}>
        <Typography variant="cap12" color={text.primary}>
          手続きおよび学習進捗を管理・閲覧できる分野を選択ください。
        </Typography>
        <Stack gap={spacing.md}>
          {publishedIndustries.map((industry) => {
            const isEnabled = !hiddenIndustryIds.some((id) => Number(id) === Number(industry.id));
            // Cannot disable an industry that has registered learners (外国人).
            const isDisabled = isEnabled && !!industry.hasLearner;
            return (
              <Stack key={industry.id} gap={1}>
                <Stack flexDirection="row" alignItems="center" gap="14px">
                  <Stack sx={{ width: "fit-content", ml: "11px" }}>
                    <Switch
                      nonControl
                      checked={isEnabled}
                      disabled={isDisabled}
                      onChange={(_, checked) => handleToggle(industry.id, checked)}
                    />
                  </Stack>
                  <Typography variant="body14" color={text.primary}>
                    {industry.name}
                  </Typography>
                </Stack>
                {isDisabled && (
                  <Typography variant="cap12" color={text.tertiary}>
                    この分野に登録された外国人が存在するため、無効にできません。
                  </Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </InnerBox>
  );
};
